// @ts-expect-error: Missing type definitions for react-force-graph-2d
import { ForceGraphMethods } from 'react-force-graph-2d'
import {
  ForceGraph,
  getScaledNodeSettings,
  useNodeHighlight,
  useSelectedNodeStore,
} from '@unholster/graphkit'
import { useToastNotification } from '@/components/compliance-assistant/ToastNotification'
import { NodeObjectType, ForceGraphInputType, NodeHoverCallback, LinkType } from './types'
import { RefObject, useRef, useState, useEffect } from 'react'
import {
  customNode,
  customEdge,
  drawEdgeLabel,
  ParticleState,
  drawParticles,
  nodeDrawingSettings,
} from './local-drawings'
import { isPep } from '@/lib/utils'

interface GraphProps {
  shownData: ForceGraphInputType
  figRef: RefObject<any>
  getGraphData: (params: {
    variables: {
      searchBy: string
      entityType: string
      depth: string
      retrieveSecondaryEntities: boolean
    }
  }) => Promise<any>
  parentDivRef: RefObject<HTMLDivElement>
  queryEntityDepth: string | null
  updateData: (nodes: NodeObjectType[], edges: LinkType[]) => void
}

function getScaleFactor(globalScale: number): number {
  return globalScale < 1 ? 1 : globalScale
}

export default function Graph({
  shownData,
  figRef,
  getGraphData,
  parentDivRef,
  queryEntityDepth,
  updateData,
}: GraphProps) {
  const { handleNodeHover, highlightLinks } = useNodeHighlight()
  const selectedNodeID = useSelectedNodeStore((state) => state.selectedNodeID)
  const { triggerToast } = useToastNotification()
  const [didFirstZoom, setDidFirstZoom] = useState(false)

  useEffect(() => {
    // Reset the zoom state when the queryEntityDepth changes.
    // queryEntityDepth consists of the string "<entity_key>-<depth>".
    if (queryEntityDepth) {
      setDidFirstZoom(false)
    }
  }, [queryEntityDepth])

  const handleDoubleClick = async (node: NodeObjectType) => {
    const searchBy = JSON.stringify({
      entity_key: node.id,
    })
    const entityType = node.category
    const depth = '1'
    const retrieveSecondaryEntities = false
    const responseGraphData = await getGraphData({
      variables: { searchBy, entityType, depth, retrieveSecondaryEntities },
    })

    if (responseGraphData.error) {
      triggerToast({ message: responseGraphData.error.message })
    } else {
      updateData(responseGraphData.data.getGraph.nodes, responseGraphData.data.getGraph.edges)
    }
  }

  const [tooltip, setTooltip] = useState<{ content: string; x: number; y: number } | null>(null)

  const onNodeHover: NodeHoverCallback = (node, _) => {
    handleNodeHover(node)

    if (node?.data?.politicamente_expuesto && figRef.current) {
      const graph = figRef.current as ForceGraphMethods
      const { x, y } = graph.graph2ScreenCoords(node.x ?? 0, node.y ?? 0)
      const isPEP = isPep(node.data.politicamente_expuesto)

      if (isPEP) {
        setTooltip({ content: 'PEP', x, y })
        return
      }
    } else {
      setTooltip(null)
    }
  }

  const labelInfoRef = useRef<any[]>([])
  const [particles, setParticles] = useState<ParticleState[]>([])

  useEffect(() => {
    // Set the initial graph forces.
    figRef.current?.d3Force('charge')?.strength(-50)
    figRef.current?.d3Force('center')?.strength(null)
  }, [figRef])

  useEffect(() => {
    // This line is needed because it forces the graph to be centered on the screen.
    // Otherwise, it would randomly move to the corners.
    figRef.current?.d3Force('center')?.strength(1)
    // Set the center force back to null after the graph has been centered.
    // 2 seconds is enough time for the graph to center.
    setTimeout(() => {
      figRef.current?.d3Force('center')?.strength(null)
    }, 2000)
  }, [shownData])

  useEffect(() => {
    if (!shownData?.links) return
    const newParticles: ParticleState[] = []
    shownData.links.forEach((link) => {
      for (let i = 0; i < 5; i++) {
        newParticles.push({
          linkId: link.id,
          t: Math.random(),
          speed: 0.006,
          width: highlightLinks.has(link.id) ? 3 : 1.75,
        })
      }
    })
    setParticles(newParticles)
  }, [shownData.links, highlightLinks])

  return (
    <div className="relative h-full w-full">
      <ForceGraph
        cooldownTicks={100}
        data={shownData}
        ref={figRef}
        onNodeDoubleClick={handleDoubleClick}
        width={parentDivRef.current?.clientWidth}
        height={parentDivRef.current?.clientHeight}
        nodeCanvasObjectMode={() => 'replace'}
        nodeCanvasObject={(node, ctx, globalScale) => {
          const isSelected = node.id === selectedNodeID
          const scaleFactor = getScaleFactor(globalScale)
          customNode(node, ctx, scaleFactor, isSelected, false, false)
        }}
        linkCanvasObjectMode={() => 'replace'}
        linkCanvasObject={(link, ctx, globalScale) => {
          const scaleFactor = getScaleFactor(globalScale)
          customEdge(link, ctx, scaleFactor, labelInfoRef)
          drawParticles(link, ctx, scaleFactor, particles)
        }}
        linkDirectionalParticles={0}
        linkDirectionalArrowLength={0}
        onEngineStop={() => {
          if (!didFirstZoom) {
            setDidFirstZoom(true)
            figRef.current.zoomToFit(400)
          }
        }}
        onNodeHover={onNodeHover}
        onRenderFramePre={() => {
          labelInfoRef.current = []

          setParticles((prev) =>
            prev.map((p) => {
              let newT = p.t + p.speed
              if (newT > 1) newT -= 1
              return { ...p, t: newT }
            })
          )
        }}
        nodePointerAreaPaint={(node, color, ctx, globalScale) => {
          const scaleFactor = getScaleFactor(globalScale)
          const { x, y, nodeRadius } = getScaledNodeSettings(nodeDrawingSettings, scaleFactor, node)
          ctx.beginPath()
          ctx.arc(x + nodeRadius, y + nodeRadius, nodeRadius, 0, 2 * Math.PI)
          ctx.fillStyle = color
          ctx.fill()
        }}
        onRenderFramePost={(ctx) => {
          labelInfoRef.current.forEach((labelData) => {
            drawEdgeLabel(ctx, labelData)
          })
        }}
      />
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x + 15,
            top: tooltip.y - 72.5,
            pointerEvents: 'none',
            backgroundColor: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.2)',
            fontSize: '12px',
            color: '#333',
            fontWeight: 'bold',
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  )
}
